/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { identity } from 'purify-ts/Function';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe, Nothing } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { assert } from '@codegrade/utils';
import * as comp from '@/components';
import { UIPrefsStore } from '@/store';
import * as utils from '@/utils';
import './style.less';
import { faAnglesDown } from '@fortawesome/free-solid-svg-icons/faAnglesDown';
import { faAnglesUp } from '@fortawesome/free-solid-svg-icons/faAnglesUp';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
export default tsx.componentFactoryOf().create({
    name: 'local-header',
    props: {
        title: p(String).default(''),
        backRoute: p.ofType().default(() => ({ type: 'empty' })),
        alwaysShowExtraSlot: p(Boolean).default(false),
        showLogo: p(Boolean).default(false),
        logoIsLink: p(Boolean).default(false),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            showExtra: false,
        };
    },
    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        darkMode() {
            return UIPrefsStore.darkMode();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        previous() {
            return Maybe.fromNullable(this.$router.getPreviousRoute());
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        controlButtonsNode() {
            return utils.ifJustOrEmpty(NonEmptyList.fromArray(Maybe.catMaybes([this.sidebarToggleNode, this.maybeBackButtonNode])), btns => (<b-input-group-prepend class="cursor-pointer flex-grow-0">
                        <b-button-group>{btns}</b-button-group>
                    </b-input-group-prepend>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        sidebarToggleNode() {
            return Maybe.fromFalsy(this.$isSmallWindow).map(() => (<b-btn variant="primary" class="sidebar-toggle" tabindex={0} onClick={this.toggleSidebar}>
                    <comp.Icon icon={faBars}/>
                </b-btn>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeBackRouteInfo() {
            const backRoute = this.backRoute;
            switch (backRoute.type) {
                case 'empty':
                    return Nothing;
                case 'back-or-empty':
                    return this.previous
                        .filter(prevRoute => this.getIsWhitelisted(prevRoute, new Set(backRoute.whitelist)))
                        .map(prevRoute => ({
                        route: prevRoute,
                        label: this.createLabel(prevRoute),
                    }));
                case 'back-or-default':
                    return this.previous
                        .filter(prevRoute => this.getIsWhitelisted(prevRoute, new Set(backRoute.whitelist)))
                        .map((prevRoute) => ({
                        route: prevRoute,
                        label: this.createLabel(prevRoute),
                    }))
                        .alt(Maybe.of({
                        route: backRoute.defaultRoute,
                        label: this.createLabel(backRoute.defaultRoute),
                    }));
                case 'to-route':
                    return Maybe.of({
                        route: backRoute.route,
                        label: this.createLabel(backRoute.route),
                    });
                default:
                    return assert.AssertionError.assertNever(backRoute);
            }
        },
        /** @returns A rendered back button, if a back route has been passed to the component. */
        maybeBackButtonNode() {
            return this.maybeBackRouteInfo.map(backRouteInfo => (<b-btn to={backRouteInfo.route} class="back-button" tabindex={0} aria-label={backRouteInfo.label} v-b-popover_bottom_hover_focus={backRouteInfo.label} role="button">
                    <comp.Icon icon={faArrowLeft}/>
                </b-btn>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        logoNode() {
            if (!this.showLogo) {
                return utils.emptyVNode();
            }
            const logo = <cg-logo inverted={!this.darkMode} class="local-header-logo"/>;
            return utils.ifExpr(this.logoIsLink, () => <router-link to="/">{logo}</router-link>, () => logo);
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getIsWhitelisted(route, whitelist) {
            return route.name != null ? whitelist.has(route.name) : false;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        createLabel(route) {
            if ('meta' in route && route.meta != null) {
                return `Back to the ${route.meta.readableName} page`;
            }
            return 'Back to previous page';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        toggleExtra() {
            this.showExtra = !this.showExtra;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        toggleSidebar() {
            this.$root.$emit('sidebar::show');
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTitle() {
            return [
                utils.renderSlot(this, 'prepend', {}),
                utils.ifOrEmpty(this.$scopedSlots.title != null || this.title !== '', () => (<h4 class="title">
                        {utils.renderSlot(this, 'title', {}, identity, () => this.title)}
                    </h4>)),
                utils.renderSlot(this, 'default', {}),
            ];
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderExtraSlotToggle() {
            const icon = this.showExtra ? faAnglesUp : faAnglesDown;
            return utils.ifOrEmpty(this.$scopedSlots.extra != null && !this.alwaysShowExtraSlot, () => (<b-input-group-append class="extra-button" v-b-popover_bottom_hover_focus={`${this.showExtra ? 'Hide' : 'Show'} more options`}>
                        <b-button onClick={this.toggleExtra}>
                            <comp.Icon icon={icon}/>
                        </b-button>
                    </b-input-group-append>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderExtraSlot() {
            return utils.renderSlot(this, 'extra', {}, node => utils.ifExpr(this.alwaysShowExtraSlot, () => (<div class="always-extra-header">
                            <hr class="separator narrow"/>
                            {node}
                        </div>), () => (<comp.Collapse id="local-header-extra" collapsed={!this.showExtra}>
                            <hr class="separator"/>
                            {node}
                        </comp.Collapse>)));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderLocalHeader() {
            return (<div class="local-header w-100">
                    <b-button-toolbar class="toolbar" justify>
                        {this.controlButtonsNode}
                        {this.renderTitle()}
                        {this.renderExtraSlotToggle()}
                        {this.logoNode}
                    </b-button-toolbar>

                    {this.renderExtraSlot()}
                </div>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderDropdown() {
            return utils.renderSlot(this, 'dropdownPanel', {}, node => (<div class="dropdown-panel">
                    <comp.Collapse id="local-header-dropdown" class="local-header-dropdown">
                        {node}
                    </comp.Collapse>
                </div>));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<div class="outer-local-header">
                {this.renderLocalHeader()}
                {this.renderDropdown()}
            </div>);
    },
});
